import { NumberInputProps } from "@mantine/core";
import { IconCurrencyEuro } from "@tabler/icons-react";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import { Control, FieldValues, Path, useController } from "react-hook-form";

import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { UnitName } from "#shared/view/designSystem/inputs/units";

interface Props<T extends FieldValues> extends NumberInputProps {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  required?: boolean;
  unit?: UnitName;
  defaultValue?: number;
  onClickWhy?: () => void;
  onChange?: (value: string | number) => void;
  onChangeWithComputedValue?: (value: string | number) => void;
  factor?: number;
}

export const PriceInputController = <T extends FieldValues>({
  name,
  control,
  label,
  required,
  disabled,
  defaultValue,
  unit,
  onClickWhy,
  onChange: onChangeCallback,
  onChangeWithComputedValue,
  factor = 1,
  ...props
}: Props<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules: { required } });

  return (
    <NumberInput
      {...field}
      label={label}
      decimalSeparator=","
      decimalScale={2}
      fixedDecimalScale
      min={0}
      unit={unit}
      leftSection={!unit && <IconCurrencyEuro size={20} />}
      value={isNumber(field.value) ? (field.value * factor) / 100 : defaultValue}
      onChange={(value) => {
        if (isNil(value)) {
          return field.onChange(undefined);
        }
        field.onChange((value / factor) * 100);
        onChangeWithComputedValue?.((value / factor) * 100);
        onChangeCallback?.(value);
      }}
      required={required}
      disabled={disabled}
      onClickWhy={onClickWhy}
      // oxlint-disable-next-line typescript/no-base-to-string
      error={error?.message?.toString() || !!error?.type}
      {...props}
    />
  );
};
